import { useRef, useState } from "react";
import { Box, Button, Form, Icon } from "react-bulma-components";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getAnalyticsConsent, setAnalyticsConsent } from "../../../firebase";
import { useEntitlements } from "../../../models/entitlements";
import { editUser, exportUserData } from "../../../models/user";
import { AppDispatch } from "../../../redux/store";
import { downloadJson } from "../../../util/download";
import { useKonamiCode } from "../../../util/useKonamiCode";
import { AdminStatsPanel } from "../../admin/AdminStatsPanel";
import { useAuthContext } from "../../auth/AuthProvider";
import { AvatarConfigPreview } from "../../sprites/AvatarConfigPreview";
import { ModalProps } from "../ModalManager";
import { showModal } from "../modalSlice";
import { modal } from "../modalTypes";

export type EditProfileModalProps = {
  name: string,
} & ModalProps;

export function EditProfileModal({ ...props }: EditProfileModalProps) {
  const { t } = useTranslation();
  const [userUid] = useAuthContext();
  const [entitlements] = useEntitlements(userUid ?? undefined);
  const [name, setName] = useState(props.name);
  const savedName = useRef(props.name);
  const [exporting, setExporting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const showUid = useKonamiCode();
  const [uidCopied, setUidCopied] = useState(false);

  const [consent, setConsent] = useState<"accepted" | "rejected">(
    getAnalyticsConsent() === "accepted" ? "accepted" : "rejected"
  );

  const handleConsentToggle = () => {
    const next: "accepted" | "rejected" = consent === "accepted" ? "rejected" : "accepted";
    setAnalyticsConsent(next);
    setConsent(next);
  };

  const handleNicknameBlur = () => {
    if (userUid === null) return;
    if (name !== savedName.current) {
      editUser(userUid, name);
      savedName.current = name;
    }
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await exportUserData();
      downloadJson("retroboard-data-export.json", data);
    } finally {
      setExporting(false);
    }
  }

  const handleDelete = () => {
    props.closeModal();
    dispatch(showModal(modal("confirmDeleteAccount")));
  }

  return (
    <Box>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "top" }}>
        <p className="title">{t("edit-user.title")}</p>
        <TierPill entitlements={entitlements} />
      </div>

      <AvatarConfigPreview onNavigateAway={props.closeModal} />

      <hr />

      <p className="subtitle">{t("nickname")}</p>
      <Form.Field>
        <Form.Control>
          <Form.Input
            type="text"
            placeholder={t("create-board.title-placeholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNicknameBlur} />
        </Form.Control>
      </Form.Field>
      {showUid && userUid && (
        <Button
          size="small"
          className="is-ghost"
          style={{ fontFamily: "monospace", fontSize: "0.7rem", textDecoration: "none", paddingLeft: 0 }}
          onClick={async () => { await navigator.clipboard.writeText(userUid); setUidCopied(true); }}
        >
          <span className="has-text-dark">{t("user-id")}</span> <span className="has-text-info">{userUid}</span>
          <Icon>
            <i className={uidCopied ? "icon-ok" : "icon-clone"} aria-hidden="true"></i>
          </Icon>
        </Button>
      )}

      <hr />

      <p className="subtitle">{t("edit-user.dataManagement")}</p>
      <div style={{ display: "flex", gap: "0.75rem" }}>
        <Button color="info is-small" loading={exporting} disabled={exporting} onClick={handleExport} style={{ flex: 1 }}>
          {t("edit-user.exportData")}
        </Button>
        <Button color="danger is-small" onClick={handleDelete} style={{ flex: 1 }}>
          {t("edit-user.deleteAccount")}
        </Button>
      </div>

      <hr />

      <p className="subtitle">{t("privacy.title")}</p>
      <p style={{ marginBottom: "0.5rem" }}>
        <strong>
          {consent === "accepted"
            ? t("privacy.analytics.state.accepted")
            : t("privacy.analytics.state.rejected")}
        </strong>
      </p>
      <p style={{ fontSize: "0.9rem", marginBottom: "0.75rem" }}>
        {consent === "accepted"
          ? t("privacy.analytics.description.accepted")
          : t("privacy.analytics.description.rejected")}
        {" "}
        <a href="/privacy">{t("privacy.analytics.descriptionPolicyLink")}</a>
      </p>
      <Button color="info is-small" onClick={handleConsentToggle}>
        {consent === "accepted"
          ? t("privacy.analytics.action.reject")
          : t("privacy.analytics.action.accept")}
      </Button>

      <AdminStatsPanel />
    </Box>
  )
}

function TierPill({ entitlements }: { entitlements: import("../../../models/entitlements").EntitlementData | undefined }) {
  const { t } = useTranslation();

  const isPro = entitlements?.pro?.active === true;
  const isSupporter = entitlements?.supporter?.active === true;

  if (!isPro && !isSupporter) return null;

  const tierKey = isPro ? "pro" : "supporter";

  return (
    <span
      className="tag is-small is-dark"
      style={{ borderRadius: 12, paddingLeft: "0.75rem", paddingRight: "0.75rem", border: "none" }}
    >
      <strong style={{ color: "inherit" }}>{t(`tier.${tierKey}`)}</strong>{"\u00A0\u00A0"}—{"\u00A0\u00A0"}{t("tier.thanks")}
    </span>
  );
}

